import { func, string } from 'prop-types';
import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';

import { Box, Grid, InputAdornment } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/styles';
import { useError } from '@avon/component-library';

import Typography from '@avon/component-library/dist/Typography';
import InputField from '@avon/component-library/dist/InputField';
import Button from '@avon/component-library/dist/Button';

import cookies from 'react-cookies';

import Dropdown from '@avon/component-library/dist/Dropdown';
import loginBg from '../styles/assets/loginBg.svg';
import avonGrowSvg from '../styles/assets/avon_grow.svg';
import avonGrowTrSvg from '../styles/assets/avon_grow_tr.svg';

import {
  // eslint-disable-next-line
  doLogin, getSlProfile, checkAgreements, getTacContent, acceptLegalInfo, getDashboard, getLoginInfo,
} from '../utils/api/endpoints';

import getDeviceType, { isMobile, isDesktop } from '../utils/getDeviceType';
import formatDate from '../utils/formatDate';
import TaC from '../components/TaC/TaC';
import closeWhiteSvg from '../styles/assets/close_white.svg';
import infoWhiteSvg from '../styles/assets/info.svg';
import { useTracking } from '../analytics/Analytics';

const useStyles = makeStyles(theme => ({
  pageContainer: {
    backgroundImage: `linear-gradient(131.14deg, ${theme.palette.colors.corePink} 4.53%, rgba(226, 25, 124, 0.8) 70.61%, rgba(229, 35, 27, 0.8) 102.64%), url(${loginBg})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    height: '100%',
    position: 'relative',
    [theme.breakpoints.between('sm', 'md')]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  cardContainer: {
    position: 'absolute',
    top: '50%',
    marginTop: '-323px',
    right: '100px',
    [theme.breakpoints.between('sm', 'md')]: {
      right: 'unset',
    },
  },
  boxContainer: {
    direction: p => (p.language === 'ar' ? 'rtl' : 'ltr'),
    background: '#FFFFFF',
    width: 454,
    height: p => (p.mrktCd === 'TR' ? 709 : 648),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderRadius: 8,
    margin: 'auto',
    padding: '48px 0',
  },
  boxContainerTablet: {
    direction: p => (p.language === 'ar' ? 'rtl' : 'ltr'),
    background: '#FFFFFF',
    width: 454,
    height: p => (p.mrktCd === 'TR' ? 709 : 646),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderRadius: 8,
    overflow: 'hidden',
    margin: 'auto',
    padding: '48px 0',
  },
  boxContainerMobile: {
    direction: p => (p.language === 'ar' ? 'rtl' : 'ltr'),
    width: '100%',
    height: 'fit-content',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    overflow: 'hidden',
    padding: '10px',
    position: 'absolute',
    margin: 'auto',
    top: 0,
    bottom: 0,
  },
  avonLogo: {
    marginTop: p => (p.deviceType === 'mobile' ? 0 : 18),
    marginBottom: 42,
  },
  marketBox: {
    marginLeft: 71,
    marginRight: 71,
  },
  forgot: {
    display: 'flex',
  },
  errorToastContainer: {
    position: 'fixed',
    top: 0,
    zIndex: 100000,
    padding: '10px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: 'Montserrat',
    color: '#ffffff',
    background: '#181818',

    width: '100%',
    margin: 0,
    [theme.breakpoints.up('768')]: {
      width: 'calc(100% - 24px)',
      margin: '0 12px',
    },
  },
  sessionExpriedDesc: {
    fontSize: 14,
    lineHeight: '18px',
  },
  placeHolder: {
    display: p => (p.deviceType === 'tablet' ? 'none' : 'block'),
  },
  InputBox: {
    marginBottom: 18,
    height: '47px',
  },
  repLinkLabelBox: {
    margin: '10px 0',
    height: '58px',
    lineHeight: '58px',
  },
  forgotPaswdUrl: {
    color: '#a2acae',
    fontSize: '16px',
    fontFamily: 'Montserrat-Medium',
    textDecoration: 'none',
    fontWeight: 500,
  },
  form: {
    maxWidth: '360px',
  },
}));

const Login = ({ callApi, defaultMarket, defaultLanguage }) => {
  const deviceType = getDeviceType();
  const mrktCd = defaultMarket || cookies.load('mrktCd');
  const langCd = defaultLanguage || cookies.load('langCd');
  const theme = useTheme();
  const usernameRef = useRef();
  const passwordRef = useRef();
  const clearRef = useRef();

  const [, setTracking] = useTracking();
  const [userAcct, setUserAcct] = useState();
  const [userPass, setUserPass] = useState();
  const [passShow, setPassShow] = useState(false);
  const { setError, cleanToastBanner } = useError();
  const [isSubmitting, setSubmitting] = useState(false);

  const [agrChecks, setAgrChecks] = useState([]);
  const [agrs, setAgrs] = useState();
  const [ver, setVer] = useState(0);
  const [showClear, setShowClear] = useState(false);
  const [showEye, setShowEye] = useState(false);
  const [sessionExpried, setSessionExpried] = useState();
  const [loginInfo, setLoginInfo] = useState({});
  const [hide, setHide] = useState(false);
  const [autoFillChanged, setAutoFillChanged] = useState(false);
  const [language, setLanguage] = useState(langCd);
  const classes = useStyles({ deviceType, language, mrktCd });

  useEffect(() => {
    sessionStorage.setItem('isWebApp', true);
    setSessionExpried(JSON.parse(sessionStorage.getItem('sessionExpired')));
    const getLogin = async () => {
      try {
        const res = await callApi(getLoginInfo(mrktCd, language));
        return res.jsonObject;
      } catch (e) {
        cookies.remove('mrktCd', { path: '/' });
        cookies.remove('langCd', { path: '/' });
        const { href } = window.location;
        window.location.replace(href.replace(`/${mrktCd}`, '').replace(`/${language}`, ''));
        setError(e.error);
        setSubmitting(false);
        return false;
      }
    };
    getLogin().then(resp => setLoginInfo(resp.loginInfo));
  }, [language]);

  useEffect(() => { }, [ver]);

  const getAgrmntContent = async (type) => {
    try {
      const res = await callApi(getTacContent(type, mrktCd, langCd));

      return res.jsonObject;
    } catch (e) {
      setError(e.error);
      setSubmitting(false);
      return false;
    }
  };

  const login = async () => {
    try {
      if (isSubmitting) return;
      setSubmitting(true);

      const res = await callApi(doLogin({
        userId: userAcct, password: userPass, mrktCd, langCd,
      }));
      sessionStorage.removeItem('reLoginMessage');
      sessionStorage.removeItem('sessionExpired');
      await sessionStorage.setItem('firstLoginPage', res.firstLoginPage);
      if (res.firstLoginPage) {
        sessionStorage.setItem('currentPage', 'landingPage');
      } else {
        sessionStorage.setItem('currentPage', 'home');
      }
      await sessionStorage.setItem('accessToken', res.accessToken);
      await sessionStorage.setItem('userId', res.userId);
      cleanToastBanner();

      const agreements = await callApi(checkAgreements(mrktCd, langCd));

      setAgrs(agreements);


      // check if any agreement not accepted, then open the TaC modal.
      const checks = agreements.userAgrmntVers.map(e => ({ ...e, checked: false }));
      if (agreements.userAgrmntVers
        && agreements.userAgrmntVers.some(e => e.isLatestVer === false)) {
        setAgrChecks(checks.reverse());
      } else {
        await callApi(getSlProfile(mrktCd, langCd)).then(slProfile => sessionStorage.setItem('slProfile', JSON.stringify(slProfile)));

        await callApi(getDashboard(sessionStorage.getItem('userId'), mrktCd, langCd)).then(dashboardData => sessionStorage.setItem('dashboard', JSON.stringify(dashboardData.jsonObject)));
        window.location.reload();
      }
      await setSubmitting(false);
      setTracking({
        event: 'login',
        market: mrktCd,
        language: langCd,
        rep_id: userAcct,
      });
    } catch (e) {
      setTracking({
        event: 'login_fails',
        market: mrktCd,
        language: langCd,
        rep_id: userAcct,
      });
      setError(e.error);
      setSubmitting(false);
    }
  };

  const nextSvg = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.29282 3.29297C5.65326 2.93245 6.22049 2.90466 6.61281 3.20964L6.70703 3.29282L10.7075 7.29238C11.068 7.65285 11.0958 8.22013 10.7907 8.61245L10.7075 8.70667L6.70711 12.7071C6.31658 13.0976 5.68342 13.0976 5.29289 12.7071C4.93241 12.3466 4.90468 11.7794 5.2097 11.3871L5.29289 11.2929L8.586 7.999L5.29297 4.70718C4.93245 4.34674 4.90466 3.77951 5.20964 3.38719L5.29282 3.29297Z" fill={theme.palette.colors.corePink} />
    </svg>
  );

  const changeMarket = async () => {
    await cookies.remove('mrktCd', { path: '/' });
    await cookies.remove('mrktName', { path: '/' });
    await cookies.remove('mrktFlag', { path: '/' });
    await cookies.remove('langCd', { path: '/' });
    window.location.reload();
  };

  const getBoxContainerClasses = () => {
    switch (deviceType) {
      case 'desktop':
        return classes.boxContainer;
      case 'tablet':
        return classes.boxContainerTablet;
      case 'mobile':
        return classes.boxContainerMobile;
      default:
        return classes.boxContainer;
    }
  };

  const handleMouseDown = (event) => {
    event.preventDefault();
  };

  const handleEnterKey = (e) => {
    if (e.nativeEvent.keyCode === 13 && userAcct && userPass) {
      login();
    }
  };

  const usernameAddonIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12ZM4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12ZM8.46447 8.46447C8.82495 8.10398 9.39218 8.07625 9.78447 8.38128L9.87868 8.46447L12 10.5858L14.1213 8.46447C14.5118 8.07394 15.145 8.07394 15.5355 8.46447C15.896 8.82495 15.9237 9.39218 15.6187 9.78447L15.5355 9.87868L13.4142 12L15.5355 14.1213C15.9261 14.5118 15.9261 15.145 15.5355 15.5355C15.175 15.896 14.6078 15.9237 14.2155 15.6187L14.1213 15.5355L12 13.4142L9.87868 15.5355C9.48816 15.9261 8.85499 15.9261 8.46447 15.5355C8.10398 15.175 8.07625 14.6078 8.38128 14.2155L8.46447 14.1213L10.5858 12L8.46447 9.87868C8.07394 9.48816 8.07394 8.85499 8.46447 8.46447Z" fill={theme.palette.colors.corePink} />
    </svg>
  );

  const eyeClose = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M20.707 3.29289C20.3165 2.90237 19.6833 2.90237 19.2928 3.29289L16.5218 6.06384C15.1249 5.37501 13.5926 5 11.9997 5C7.85059 5 4.11296 7.5442 2.10499 11.5513C1.96374 11.8332 1.9637 12.1652 2.10488 12.4471C3.00457 14.2436 4.25198 15.7462 5.72834 16.8574L3.2928 19.2929L3.20961 19.3871C2.90458 19.7794 2.93231 20.3466 3.2928 20.7071C3.68332 21.0976 4.31649 21.0976 4.70701 20.7071L7.4778 17.9363C8.87469 18.625 10.4068 19 11.9997 19C16.1487 19 19.8863 16.4558 21.8943 12.4487C22.0356 12.1668 22.0356 11.8348 21.8944 11.5529C20.9948 9.75651 19.7475 8.25403 18.2713 7.14286L20.707 4.70711L20.7902 4.6129C21.0952 4.22061 21.0675 3.65338 20.707 3.29289ZM16.531 8.88313L16.8397 8.57438C18.0052 9.39668 19.0178 10.5154 19.7951 11.869L19.8679 12L19.7951 12.1321C18.0612 15.15 15.1576 17 11.9997 17L11.7043 16.9946C10.7671 16.9603 9.85515 16.7632 8.99268 16.4214L9.88303 15.5311C10.5259 15.8319 11.2433 16 11.9999 16C14.7613 16 16.9999 13.7614 16.9999 11C16.9999 10.2434 16.8318 9.52597 16.531 8.88313ZM13.904 8.68165C13.3861 8.25572 12.7229 8 11.9999 8C10.3431 8 8.9999 9.34315 8.9999 11C8.9999 11.7229 9.25563 12.3862 9.68155 12.9041L13.904 8.68165ZM11.4622 13.9519L14.9518 10.4623C14.9834 10.6367 14.9999 10.8164 14.9999 11C14.9999 12.6569 13.6568 14 11.9999 14C11.8163 14 11.6366 13.9835 11.4622 13.9519ZM8.2633 14.3224C7.47737 13.4391 6.9999 12.2753 6.9999 11C6.9999 9.91047 7.34839 8.90234 7.93998 8.08097C6.44548 8.92142 5.15061 10.2207 4.20423 11.8679L4.1309 11.999L4.20422 12.131C4.98156 13.4847 5.99432 14.6035 7.15987 15.4258L8.2633 14.3224Z" fill={theme.palette.colors.corePink} />
    </svg>
  );

  const eyeOpen = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M12.0006 5C7.85156 5 4.11394 7.5442 2.10597 11.5513C1.96472 11.8332 1.96468 12.1652 2.10586 12.4471C4.11305 16.4551 7.85097 19 12.0006 19C16.1497 19 19.8873 16.4558 21.8953 12.4487C22.0365 12.1668 22.0366 11.8348 21.8954 11.5529C19.8882 7.54491 16.1503 5 12.0006 5ZM16.0612 8.08154C17.5555 8.92218 18.8501 10.2216 19.7961 11.869L19.8689 12L19.7961 12.1321C18.0622 15.15 15.1586 17 12.0006 17L11.7053 16.9946C8.66195 16.8832 5.88434 15.0552 4.2052 12.131L4.13188 11.999L4.2052 11.8679C5.15159 10.2207 6.44646 8.92142 7.94095 8.08097C7.34937 8.90234 7.00088 9.91047 7.00088 11C7.00088 13.7614 9.23946 16 12.0009 16C14.7623 16 17.0009 13.7614 17.0009 11C17.0009 9.91072 16.6526 8.90279 16.0612 8.08154ZM12.0009 8C13.6577 8 15.0009 9.34315 15.0009 11C15.0009 12.6569 13.6577 14 12.0009 14C10.344 14 9.00088 12.6569 9.00088 11C9.00088 9.34315 10.344 8 12.0009 8Z" fill={theme.palette.colors.corePink} />
    </svg>
  );

  const justineLogo = (
    <svg width="141" height="58" viewBox="0 0 141 58" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M139.723 32.0702C130.364 40.609 126.439 40.2476 123.416 38.686C122.129 38.0815 121.831 35.9674 123.615 34.6595C132.222 29.0855 134.084 18.0241 123.283 25.0565C121.001 26.598 117.865 30.0873 116.612 36.3714C107.908 39.9005 107.607 37.2619 108.593 32.172C111.224 24.3497 109.31 23.4361 102.855 29.4278C101.444 27.9519 100.357 28.0712 98.6341 29.7709L91.2069 35.5148C84.1317 40.1167 82.8195 39.049 86.4054 32.0462C89.832 26.7848 89.3294 24.7377 83.7787 30.0285C83.2787 30.9249 82.8209 31.7726 82.3928 32.5848C67.8998 46.1704 69.6049 38.3271 80.307 18.7961C106.334 17.6777 134.945 16.6457 134.945 16.6457L136.272 13.4037C136.272 13.4037 107.477 13.8772 84.6222 14.5093C81.1618 14.7658 82.2049 14.5131 84.0188 10.9075C86.1615 6.58314 84.7675 5.79074 82.4279 7.30718C81.0715 8.02606 80.6222 9.74727 79.7267 11.6795C79.1085 13.4073 78.4338 14.2645 77.7012 14.2514L62.7163 14.671L60.6522 19.3107C67.591 18.349 72.0566 18.2621 76.3561 18.5938L67.9111 36.5435C64.1244 46.9961 68.3585 48.9594 79.8419 37.9208C76.2598 46.6102 80.1566 46.5274 91.5441 37.6602C94.5936 35.3027 95.3503 35.8184 93.8229 39.2015C92.9949 41.5168 94.0189 44.0574 96.0926 41.8403L98.9659 37.944C102.174 34.0844 102.582 33.9281 102.498 38.5572C102.805 43.3791 107.303 44.5925 113.322 41.3439C115.569 40.0894 116.375 39.2396 117.643 40.9853C123.313 46.5868 133.311 43.6449 140.485 33.1844C141.118 31.5389 140.864 31.1662 139.723 32.0702ZM122.351 30.0285L123.872 27.2848C124.884 25.8269 126.235 27.1127 125.391 29.1705L124.545 30.285C123.028 32.9426 121.733 32.3999 122.351 30.0285ZM58.178 24.9582C56.5147 26.9923 55.9861 28.2719 55.9151 29.443C55.7266 30.4661 56.1403 30.1516 54.8144 31.0518L51.3283 33.579C41.3425 41.3134 40.5522 38.0537 48.2933 26.0164C49.4623 23.6425 44.4906 25.5165 41.5567 28.5689C41.1318 31.2212 39.8381 33.4499 37.673 35.2588C33.6939 39.2015 31.4955 38.8739 33.6939 34.4003L36.66 28.0557C38.3544 24.6301 37.5047 24.2718 33.525 26.8556C31.2127 28.243 31.1551 28.5458 29.9807 31.6568L26.2657 40.5736C25.0087 44.126 26.7391 44.697 30.2456 42.6322L34.4657 40.06C37.3283 37.7101 38.4533 37.9391 37.8424 40.7454C37.7658 44.1277 41.6207 44.6371 45.7762 42.1178L51.8802 36.1237C54.1435 33.8505 56.0681 32.953 57.6157 34.5639C59.3905 35.9654 60.255 37.5184 60.6319 38.8586C61.1833 40.228 59.9955 42.2014 58.4378 40.5736C58.2451 38.9259 57.3272 38.9441 55.7363 40.2318C54.7351 40.9167 54.0263 41.5005 54.0472 43.1457C54.3467 46.0008 56.5989 45.8859 60.8008 42.8043C67.7478 37.6504 68.649 33.4774 63.5025 30.285C61.3095 28.9627 60.9753 28.109 63.3331 26.1683C66.4858 23.4557 63.2294 19.98 58.178 24.9582ZM39.0806 0L33.1594 0.697634C31.8703 0.885793 31.0741 0.944066 30.8509 2.51115C25.4478 24.4782 23.1757 44.1464 0 58C29.4829 49.9965 39.0806 0 39.0806 0Z" fill={theme.palette.colors.corePink} />
    </svg>
  );

  const SessionExpiredToast = useCallback(() => {
    const { loginPageToast, loginPageToastDesktop } = sessionExpried || {};
    return (
      <>
        <Box
          className={classes.errorToastContainer}
          style={{
            display: sessionExpried ? 'flex' : 'none',
          }}
        >
          <Box><img src={infoWhiteSvg} alt="info" /></Box>
          <Box textAlign="center">
            <Box className={classes.sessionExpriedDesc}>
              {isDesktop() ? loginPageToastDesktop : loginPageToast}
            </Box>
          </Box>
          <Box
            style={{ cursor: 'pointer' }}
            onClick={
              () => {
                sessionStorage.removeItem('sessionExpired');
                setSessionExpried(false);
              }
            }
          >
            <img src={closeWhiteSvg} alt="close" />
          </Box>
        </Box>
      </>
    );
  }, [sessionExpried]);

  const mrktFlag = cookies.load('mrktFlag');

  const formBox = mrktFlag && (
    <Box className={getBoxContainerClasses()}>
      {isMobile() ? null : (
        <Grid>
          <Typography
            weight="light"
            style={{ fontSize: '18px', color: '#181818', fontFamily: 'Montserrat' }}
          >
            {loginInfo.heading}
          </Typography>
        </Grid>
      )}
      <Grid className={classes.avonLogo}>
        {mrktCd === 'JT' ? justineLogo : (
          <img alt="" style={{ width: 160, height: 75 }} src={language === 'ar' ? avonGrowTrSvg : avonGrowSvg} />)}
      </Grid>
      <Grid className={classes.form}>
        {isMobile() ? null : (
          <Box style={{ textAlign: 'center' }}>
            <Typography
              weight="light"
              style={{ fontSize: '16px', color: '#707677', fontFamily: 'Montserrat' }}
            >
              {loginInfo.subheading}
            </Typography>
          </Box>
        )}
        <Box style={{ marginTop: isMobile() ? 0 : 30 }}>
          <Box style={{ marginBottom: 18 }}>
            {
              loginInfo.selectLanguage && mrktCd === 'TR'
                ? (
                  <Dropdown
                    fullWidth
                    // placeholder={loginInfo.selectLanguage.name}
                    selectOptions={
                      loginInfo.selectLanguage.languages.map(l => ({ label: `${l.langNm} (${l.langDsplyNm})`, value: l.langCd }))
                    }
                    defaultValue={language}
                    onChange={async (value) => {
                      setLanguage(value);
                      // sessionStorage.setItem('langCd', value);
                      cookies.save('langCd', value, { path: '/' });
                      cookies.save('langDisplay',
                        loginInfo.selectLanguage.languages.filter(
                          m => m.langCd === value,
                        )[0].langDsplyNm, { path: '/' });
                      const { origin, pathname } = window.location;
                      window.location.href = origin + pathname.replace(`/${langCd}`, `/${value}`);
                    }}
                    MenuWidth="305px"
                    selectStyle={{ height: '47px', alignItems: 'flex-end' }}
                    menuItemStyle={{
                      color: '#181818',
                      justifyContent: language === 'ar' ? 'flex-end' : 'flex-start',
                    }}
                  />
                )
                : null
            }
          </Box>
          <Box className={classes.InputBox}>
            <InputField
              inputRef={usernameRef}
              fullWidth
              labelSize={12}
              name="login-name"
              id="login-name"
              label={showClear ? loginInfo.accountNumber : ''}
              placeholder={showClear ? loginInfo.accountNumberHint : loginInfo.accountNumber}
              onChange={(e) => {
                setUserAcct(e.target.value);
                setAutoFillChanged(true);
              }}
              onFocus={() => setShowClear(true)}
              onBlur={() => setShowClear(false)}
              inputProps={{
                onKeyDown: handleEnterKey,
                style: { height: '34px', fontSize: '16px', paddingLeft: 6 },
              }}
              endAdornment={(
                <InputAdornment position="end">
                  <Box
                    id="usernameAddonIcon"
                    ref={clearRef}
                    style={{
                      position: 'relative',
                      left: 'calc(100% - 24px)',
                      cursor: 'pointer',
                      display: showClear ? 'block' : 'none',
                    }}
                    onFocus={() => setShowClear(true)}
                    onClick={
                      () => {
                        usernameRef.current.value = '';
                        passwordRef.current.value = '';
                        usernameRef.current.focus();
                        setUserAcct('');
                        setUserPass('');
                      }
                    }
                    onMouseDown={handleMouseDown}
                  >
                    {showClear ? usernameAddonIcon : ''}
                  </Box>
                </InputAdornment>
              )}
            />

          </Box>
          <Box className={classes.InputBox}>
            <InputField
              inputRef={passwordRef}
              fullWidth
              labelSize={12}
              name="login-password"
              id="login-password"
              label={showEye ? loginInfo.password : ''}
              placeholder={showEye ? loginInfo.passwordHint : loginInfo.password}
              type={passShow ? 'text' : 'password'}
              onFocus={() => setShowEye(true)}
              onBlur={() => setShowEye(false)}
              onChange={(e) => {
                setUserPass(e.target.value);
                setAutoFillChanged(true);
              }}
              inputProps={{
                onKeyDown: handleEnterKey,
                style: { height: '34px', fontSize: '16px', paddingLeft: 6 },
              }}
              endAdornment={(
                <InputAdornment position="end">
                  <Box
                    id="passwordAddonIcon"
                    onClick={() => setPassShow(!passShow)}
                    onMouseDown={handleMouseDown}
                    style={{
                      position: 'relative',
                      left: 'calc(100% - 24px)',
                      display: showEye ? 'block' : 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {passShow ? eyeOpen : eyeClose}
                  </Box>
                </InputAdornment>
              )}
            />
          </Box>
        </Box>
        <Box className={classes.forgot}>
          <div style={{ transform: language === 'ar' ? 'rotateY(180deg)' : 'unset' }}>
            {nextSvg}
          </div>
          <Typography style={{ marginLeft: language === 'ar' ? 'unset' : 8, marginRight: language === 'ar' ? 8 : 'unset' }}>
            <a href={loginInfo.forgetPasswordUrl} className={classes.forgotPaswdUrl}>
              {loginInfo.forgotPassword}
            </a>
          </Typography>
        </Box>
        <Box mt={5} style={{ marginTop: 32 }}>
          <Button
            priority="primary"
            name="btn-login"
            id="btn-login"
            onClick={login}
            disabled={(!userAcct || !userPass) && (!document.querySelector('input:-webkit-autofill') || autoFillChanged)}
            loading={isSubmitting}
          >
            {loginInfo.loginButtonLabel}
          </Button>
        </Box>
        <Box className={classes.repLinkLabelBox}>
          <Typography style={{
            margin: '17px 48px',
            fontWeight: 'bold',
            fontFamily: 'Montserrat',
            fontSize: 16,
            textAlign: 'center',
          }}
          >
            <a
              href={loginInfo.repLink}
              style={{ textDecoration: 'none', color: theme.palette.colors.corePink, whiteSpace: 'nowrap' }}
            >
              {loginInfo.repLinkLabel}
            </a>
          </Typography>
        </Box>
        {mrktCd !== 'JT' && (
          <Box style={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }} onClick={changeMarket}>
            <div style={{
              marginLeft: language === 'ar' ? 11 : 76,
              marginRight: language === 'ar' ? 76 : 11,
              width: 28,
              height: 20,
              background: `url(${cookies.load('mrktFlag')})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 100%',
            }}
            />
            <Box style={{ marginRight: language === 'ar' ? 'unset' : 76, marginLeft: language === 'ar' ? 76 : 'unset' }}>
              <Typography weight="bold">
                {cookies.load('mrktName')}
              </Typography>
            </Box>
          </Box>
        )}
      </Grid>

      {agrChecks && agrChecks.length !== 0 && (
        <>
          {
            agrChecks[agrChecks.length - 1].agrmntTyp
            && (
              <TaC
                key={agrChecks[agrChecks.length - 1].agrmntTyp}
                agrType={agrChecks[agrChecks.length - 1].agrmntTyp}
                getAgrmntContent={getAgrmntContent}
                saveLabel={loginInfo.tcAccept || 'Accept'}
                cancelLabel={loginInfo.tcDecline || 'Decline'}
                title="title"
                submitData={async () => {
                  // eslint-disable-next-line
                  agrChecks.splice(agrChecks.findIndex(item => item.agrmntTyp === agrChecks[agrChecks.length - 1].agrmntTyp), 1);

                  if (agrChecks.length === 0) {
                    try {
                      setHide(true);
                      setSubmitting(true);

                      const agrArray = [];
                      agrs.userAgrmntVers.map(a => agrArray.push(
                        {
                          agrmntTyp: a.agrmntTyp,
                          agrmntVerNr: a.latestVerNr,
                          agrmntAcptdDt: formatDate(new Date(), langCd, 'YYYY-MM-DDTHH:mm:ss'),
                          agrmntAcptdInd: true,
                        },
                      ));

                      // accept the terms and conditions
                      await callApi(acceptLegalInfo({ userAgrmnts: agrArray }));

                      // get the sl profile and redirect to the home page
                      await callApi(getSlProfile(mrktCd, langCd)).then(slProfile => sessionStorage.setItem('slProfile', JSON.stringify(slProfile)));
                      await sessionStorage.setItem('userId', userAcct);
                      await callApi(getDashboard(sessionStorage.getItem('userId'), mrktCd, langCd)).then(dashboardData => sessionStorage.setItem('dashboard', JSON.stringify(dashboardData.jsonObject)));
                      await sessionStorage.setItem('isWebApp', true);
                      window.location.reload();
                      setSubmitting(false);
                      return true;
                    } catch (e) {
                      setHide(false);
                      setError(e);
                      setSubmitting(false);
                      return false;
                    }
                  } else {
                    setVer(ver + 1);
                    return true;
                  }
                }}
                isSubmitting={false}
                open={!agrChecks[agrChecks.length - 1].checked}
                closeModal={() => {
                  setAgrChecks([]);
                  setUserAcct('');
                  usernameRef.current.value = '';
                  setUserPass('');
                  passwordRef.current.value = '';
                  setVer(ver + 1);
                  sessionStorage.removeItem('accessToken');
                }}
              />
            )
          }
        </>
      )}
      <SessionExpiredToast />
    </Box>
  );

  return hide ? null : (
    <>
      {deviceType === 'mobile' ? (formBox) : (
        <div className={classes.pageContainer}>
          <div className={classes.cardContainer}>
            {formBox}
          </div>
          <SessionExpiredToast />
        </div>
      )}
    </>
  );
};

Login.propTypes = {
  callApi: func.isRequired,
  defaultMarket: string.isRequired,
  defaultLanguage: string.isRequired,
};

export default Login;
